import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { createDropDownItems } from '@feature/user-preferences/setting-types/boolean-setting';
import { ISettingDescriptor, IDropDownSettingDescriptor,
         IPeriodicEmailSettingsTuple, ChangeTrackedValue } from '@feature/user-preferences/setting-types/common';
import { createDefaultPeriodicEmailSettings, DefaultTimeString,
         IPeriodicEmailSettings, isPartiallyInitialized } from '@feature/user-preferences/setting-types/periodic-email-settings';
import { DayOfWeek, DisplayMode, PeriodicEmailFrequency } from './common';

/*
This component is re-used for both the system settings and the user preferences
but has 2 display modes for each context i.e.

  1. System Defaults
  2. User Preferences

The difference in display mode is that System Defaults is simpler, being only
the editor for the system default settings. Whereas the User Preferences
mode, as well as providing editing and display of user preferences if set, will
also show the system default settings when the option to use system defaults
and not override with user preferences is selected. This choice is available
by using a drop-down representing a tri-state drop-down as the top option
for whether to send periodic email or not when in user preferences mode.

So to summarize, whilst both display modes share the same set of controls, 
the key difference is -

• For system defaults display mode 

  The top option is just a boolean switch to indicate whether periodic emails
  should be sent or not i.e. 
  1) Send periodic email - i.e. true
  2) Don't send periodic email - i.e. false

• For user preferences display mode

  The top option is a tri-state boolean i.e.
  1) Use System Defaults (as shown by the disabled controls, so when selected this
      option means that user preferences are not stored so that system defaults 
      take precedence) i.e. null for the whole user preferences object
  2) Send periodic email - i.e. true
  3) Don't send periodic email - i.e. false
*/

@Component({
  selector: 'mojo-periodic-email-settings',
  templateUrl: './periodic-email-settings.component.html'
})
export class PeriodicEmailSettingsComponent implements OnInit {

  DM = DisplayMode;

  @Input() displayMode: DisplayMode;

  @Input() settingsTuple: IPeriodicEmailSettingsTuple;

  @Output('settingsChanged') settingsChanged = new EventEmitter<IPeriodicEmailSettings>;

  readonly sendPeriodicEmailUP: IDropDownSettingDescriptor = {
    controlId: "send-periodic-email-up",
    controlLabelText: "Receive periodic email notification of assigned forms",
    options: null,
    value: null,
    disabled: false,
    hidden: false
  }
  readonly sendPeriodicEmailSD: ISettingDescriptor = {
    controlId: "send-periodic-email-sd",
    controlLabelText: "Send periodic Email notification to form assignees (Default)",
    value: null,
    disabled: false,
    hidden: false
  }
  readonly includeOnlyNew: ISettingDescriptor = {
    controlId: "include-only-new",
    controlLabelText: "Only forms not included previously",
    value: null,
    disabled: false,
    hidden: false
  }
  readonly frequency: IDropDownSettingDescriptor = {
    controlId: "frequency",
    controlLabelText: "Frequency",
    options: [
      { label: 'Once weekly', value: PeriodicEmailFrequency.OnceWeekly },
      { label: 'Once daily', value: PeriodicEmailFrequency.OnceDaily },
      { label: 'Twice daily', value: PeriodicEmailFrequency.TwiceDaily }
    ],
    value: null,
    disabled: false,
    hidden: false
  }
  readonly weekday: IDropDownSettingDescriptor = {
    controlId: "weekday",
    controlLabelText: "Weekday",
    options: [
      { label: 'Sunday', value: DayOfWeek.Sunday },
      { label: 'Monday', value: DayOfWeek.Monday },
      { label: 'Tuesday', value: DayOfWeek.Tuesday },
      { label: 'Wednesday', value: DayOfWeek.Wednesday },
      { label: 'Thursday', value: DayOfWeek.Thursday },
      { label: 'Friday', value: DayOfWeek.Friday },
      { label: 'Saturday', value: DayOfWeek.Saturday },
    ],
    value: null,
    disabled: false,
    hidden: false
  }
  readonly timeString1: ISettingDescriptor = {
    controlId: "time-string-1",
    controlLabelText: "Times to send (24H)",
    value: null,
    disabled: false,
    hidden: false
  }
  readonly timeString2: ISettingDescriptor = {
    controlId: "time-string-2",
    controlLabelText: "",
    value: null,
    disabled: false,
    hidden: false
  }

  constructor() { }

  ngOnInit(): void {

    if (this.displayMode == DisplayMode.UserPreferences) {

      this.sendPeriodicEmailUP.options =
        createDropDownItems(this.settingsTuple.SystemDefaults.SendPeriodicEmail)

      // initialize user preferences for possible use by dialog controls
      // it will be discarded if settings are kept as, or reverted to, system defaults
      let useSystemDefaults = this.settingsTuple.UserPreferences == null ||
                              this.settingsTuple.UserPreferences.SendPeriodicEmail == null
      if (this.settingsTuple.UserPreferences == null) {
        this.settingsTuple.UserPreferences = createDefaultPeriodicEmailSettings()
      } else if (isPartiallyInitialized(this.settingsTuple.UserPreferences)) {
        this.settingsTuple.UserPreferences = createDefaultPeriodicEmailSettings()
      }
      if (useSystemDefaults) {
        this.settingsTuple.UserPreferences.SendPeriodicEmail = null
      }
      if (isPartiallyInitialized(this.settingsTuple.SystemDefaults))
        this.settingsTuple.SystemDefaults = createDefaultPeriodicEmailSettings()

      let sendPeriodicEmail = this.settingsTuple.UserPreferences.SendPeriodicEmail
      this.sendPeriodicEmailUP.value = new ChangeTrackedValue<boolean>(sendPeriodicEmail)
    
      let settingsToUse = 
        useSystemDefaults
          ? this.settingsTuple.SystemDefaults
          : this.settingsTuple.UserPreferences
  
      let disabled = sendPeriodicEmail == null || sendPeriodicEmail == false
  
      let hidden = (sendPeriodicEmail == null && !this.settingsTuple.SystemDefaults.SendPeriodicEmail)
                   || sendPeriodicEmail == false

      this.initControls(settingsToUse, disabled, hidden)

    } else if (this.displayMode == DisplayMode.SystemDefaults) {

      this.sendPeriodicEmailSD.value = 
        new ChangeTrackedValue<boolean>(this.settingsTuple.SystemDefaults.SendPeriodicEmail)

      if (isPartiallyInitialized(this.settingsTuple.SystemDefaults))
        this.settingsTuple.SystemDefaults = createDefaultPeriodicEmailSettings()

      let disabled = this.settingsTuple.SystemDefaults.SendPeriodicEmail == null || 
                     this.settingsTuple.SystemDefaults.SendPeriodicEmail == false

      let hidden = this.settingsTuple.SystemDefaults.SendPeriodicEmail == false
  
      this.initControls(this.settingsTuple.SystemDefaults, disabled, hidden)
    }

  }

  initControls(settingsToUse: IPeriodicEmailSettings,
               disabled: boolean,
               hidden: boolean): void {

    this.includeOnlyNew.value = 
      new ChangeTrackedValue<boolean>(settingsToUse.IncludeOnlyNew)
    this.includeOnlyNew.disabled = disabled
    this.includeOnlyNew.hidden = hidden

    this.frequency.value = 
      new ChangeTrackedValue<PeriodicEmailFrequency>(settingsToUse.Frequency)
    this.frequency.disabled = disabled
    this.frequency.hidden = hidden

    let dayOfWeek = settingsToUse.Weekdays 
      ? settingsToUse.Weekdays.length > 0
        ? settingsToUse.Weekdays[0]
        : null
      : null

    this.weekday.value = new ChangeTrackedValue<any>(dayOfWeek)
    this.weekday.disabled = disabled
    this.weekday.hidden = hidden || settingsToUse.Frequency != PeriodicEmailFrequency.OnceWeekly

    this.timeString1.value = new ChangeTrackedValue<string>(settingsToUse.Times 
      ? settingsToUse.Times.length > 0
        ? settingsToUse.Times[0]
        : DefaultTimeString
      : DefaultTimeString)
    this.timeString1.disabled = disabled;
    this.timeString1.hidden = hidden;

    this.timeString2.value = new ChangeTrackedValue<string>(settingsToUse.Times 
      ? settingsToUse.Times.length > 1
        ? settingsToUse.Times[1]
        : DefaultTimeString
      : DefaultTimeString)
    this.timeString2.disabled = disabled;
    this.timeString2.hidden = hidden || settingsToUse.Frequency != PeriodicEmailFrequency.TwiceDaily
  }

  sendPeriodicEmailUPChanged(e): void {

    // Resets the display setup as follows for the stated values -
    // 1) null i.e. use system default 
    //    use the controls to show the system defaults
    //    but the controls should be disabled
    // 2) true 
    //    the controls are available and editable
    //    for editing the user preferences
    // 3) false
    //    the controls are not shown

    let sendPeriodicEmail = e.value
    
    let settingsToUse = 
      sendPeriodicEmail == null
        ? this.settingsTuple.SystemDefaults
        : this.settingsTuple.UserPreferences

    let disabled = sendPeriodicEmail == null || sendPeriodicEmail == false

    let hidden = (sendPeriodicEmail == null && !this.settingsTuple.SystemDefaults.SendPeriodicEmail)
                  || sendPeriodicEmail == false

    // reset the controls back to their originally supplied values
    this.initControls(settingsToUse, disabled, hidden)

    // effectively only the parent boolean is the subject of the notification here
    this.emitSettingsChanged()
  }

  sendPeriodicEmailSDChanged(e): void {

    // Resets the display setup as follows for the stated values -
    // 1) true 
    //    the controls are available and editable
    //    for editing the system defaults
    // 2) false
    //    the controls are not shown

    let sendPeriodicEmail = e.checked

    let disabled = sendPeriodicEmail == null || sendPeriodicEmail == false

    let hidden = sendPeriodicEmail == false

    // reset the controls back to their originally supplied values
    this.initControls(this.settingsTuple.SystemDefaults, disabled, hidden)

    this.emitSettingsChanged()
  }

  includeOnlyNewChanged(e): void {
    this.emitSettingsChanged()
  }

  weekdayChanged(e): void {
    this.emitSettingsChanged()
  }

  frequencyChanged(e): void {
    this.weekday.hidden = e.value != PeriodicEmailFrequency.OnceWeekly
    this.timeString2.hidden = e.value != PeriodicEmailFrequency.TwiceDaily
    this.emitSettingsChanged()
  }

  timeString1Changed(e): void {
    this.timeString1.value.current = e
    this.emitSettingsChanged()
  }

  timeString2Changed(e): void {
    this.timeString2.value.current = e
    this.emitSettingsChanged()
  }

  emitSettingsChanged(): void {

    let sendPeriodicEmail = 
      this.displayMode == DisplayMode.UserPreferences
        ? this.sendPeriodicEmailUP
        : this.sendPeriodicEmailSD

    let times = [ this.timeString1.value.current ]
    if (this.frequency.value.current == PeriodicEmailFrequency.TwiceDaily && 
          this.timeString2.value.current) {
      times.push(this.timeString2.value.current)
    }

    let pes = <IPeriodicEmailSettings> {
      SendPeriodicEmail: sendPeriodicEmail.value.current,
      IncludeOnlyNew: this.includeOnlyNew.value.current,
      Frequency: this.frequency.value.current,
      Weekdays: this.weekday.value.current
        ? [ this.weekday.value.current ]
        : [],
      Times: times
    }
    this.settingsChanged.emit(pes)
  }

  changed(): boolean {

    let sendPeriodicEmailChanged =
      this.displayMode == DisplayMode.UserPreferences
        ? this.sendPeriodicEmailUP.value.changed()
        : this.sendPeriodicEmailSD.value.changed()

    let weekdayChanged = 
      this.frequency.value.current == PeriodicEmailFrequency.OnceWeekly &&
        this.weekday.value.changed()

    let time2Changed = 
      this.frequency.value.current == PeriodicEmailFrequency.TwiceDaily &&
        this.timeString2.value.changed()
    
    return sendPeriodicEmailChanged ||
      this.includeOnlyNew.value.changed() ||
      this.frequency.value.changed() ||
      weekdayChanged ||
      this.timeString1.value.changed() ||
      time2Changed
  }


}
